/** 与客户端 VapConfig 对齐；分辨率按 fullscreenType 在 fullscreenType.ts 中区分 */
export const VAP_ORIGINAL_SIZE = 512;
export const VAP_PREVIEW_SIZE = 304;

/** original：VapSpec(fps=30, bitrateKbps=3000, maxDurationSec=15) */
export const VAP_ORIGINAL_SPEC = {
  size: VAP_ORIGINAL_SIZE,
  fps: 30,
  bitrateKbps: 3000,
  maxDurationSec: 15,
} as const;

/** preview：VapSpec(fps=30, bitrateKbps=2000, maxDurationSec=3) */
export const VAP_PREVIEW_SPEC = {
  size: VAP_PREVIEW_SIZE,
  fps: 30,
  bitrateKbps: 2000,
  maxDurationSec: 3,
} as const;

export const VAP_MAX_DURATION_SEC_MIN = 1;
export const VAP_MAX_DURATION_SEC_MAX = 15;

export const VAP_FPS_MIN = 1;
export const VAP_FPS_MAX = 60;

export const VAP_BITRATE_KBPS_MIN = 100;
export const VAP_BITRATE_KBPS_MAX = 10000;

/** 原图 / 缩略图可配置的编码参数（分辨率固定，不在此配置） */
export interface VapEncodeLimits {
  originalFps: number;
  originalBitrateKbps: number;
  originalMaxDurationSec: number;
  previewFps: number;
  previewBitrateKbps: number;
  previewMaxDurationSec: number;
}

/** @deprecated 使用 VapEncodeLimits；保留以兼容旧调用 */
export type VapDurationLimits = Pick<
  VapEncodeLimits,
  "originalMaxDurationSec" | "previewMaxDurationSec"
>;

export const DEFAULT_VAP_ENCODE_LIMITS: VapEncodeLimits = {
  originalFps: VAP_ORIGINAL_SPEC.fps,
  originalBitrateKbps: VAP_ORIGINAL_SPEC.bitrateKbps,
  originalMaxDurationSec: VAP_ORIGINAL_SPEC.maxDurationSec,
  previewFps: VAP_PREVIEW_SPEC.fps,
  previewBitrateKbps: VAP_PREVIEW_SPEC.bitrateKbps,
  previewMaxDurationSec: VAP_PREVIEW_SPEC.maxDurationSec,
};

export const DEFAULT_VAP_DURATION_LIMITS: VapDurationLimits = {
  originalMaxDurationSec: DEFAULT_VAP_ENCODE_LIMITS.originalMaxDurationSec,
  previewMaxDurationSec: DEFAULT_VAP_ENCODE_LIMITS.previewMaxDurationSec,
};

function clampInt(value: number, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function sanitizeVapMaxDurationSec(value: number): number {
  return clampInt(
    value,
    VAP_MAX_DURATION_SEC_MIN,
    VAP_MAX_DURATION_SEC_MAX,
    VAP_MAX_DURATION_SEC_MIN,
  );
}

export function sanitizeVapFps(value: number): number {
  return clampInt(value, VAP_FPS_MIN, VAP_FPS_MAX, VAP_ORIGINAL_SPEC.fps);
}

export function sanitizeVapBitrateKbps(value: number): number {
  return clampInt(
    value,
    VAP_BITRATE_KBPS_MIN,
    VAP_BITRATE_KBPS_MAX,
    VAP_ORIGINAL_SPEC.bitrateKbps,
  );
}

export function normalizeVapEncodeLimits(
  limits?: Partial<VapEncodeLimits>,
): VapEncodeLimits {
  return {
    originalFps: sanitizeVapFps(
      limits?.originalFps ?? DEFAULT_VAP_ENCODE_LIMITS.originalFps,
    ),
    originalBitrateKbps: sanitizeVapBitrateKbps(
      limits?.originalBitrateKbps ?? DEFAULT_VAP_ENCODE_LIMITS.originalBitrateKbps,
    ),
    originalMaxDurationSec: sanitizeVapMaxDurationSec(
      limits?.originalMaxDurationSec ??
        DEFAULT_VAP_ENCODE_LIMITS.originalMaxDurationSec,
    ),
    previewFps: sanitizeVapFps(
      limits?.previewFps ?? DEFAULT_VAP_ENCODE_LIMITS.previewFps,
    ),
    previewBitrateKbps: sanitizeVapBitrateKbps(
      limits?.previewBitrateKbps ?? DEFAULT_VAP_ENCODE_LIMITS.previewBitrateKbps,
    ),
    previewMaxDurationSec: sanitizeVapMaxDurationSec(
      limits?.previewMaxDurationSec ??
        DEFAULT_VAP_ENCODE_LIMITS.previewMaxDurationSec,
    ),
  };
}

/** @deprecated 使用 normalizeVapEncodeLimits */
export function normalizeVapDurationLimits(
  limits?: Partial<VapDurationLimits>,
): VapDurationLimits {
  const full = normalizeVapEncodeLimits(limits);
  return {
    originalMaxDurationSec: full.originalMaxDurationSec,
    previewMaxDurationSec: full.previewMaxDurationSec,
  };
}

export function vapFrameCount(durationSec: number, fps: number): number {
  return Math.max(2, Math.round(durationSec * fps));
}

export function vapGopWholeSec(frameCount: number, fps: number): number {
  return Math.ceil(frameCount / fps) + 1;
}

export function align16(value: number): number {
  return Math.ceil(value / 16) * 16;
}

/** AVC Level 3.1 最大编码面积 921600；9:16 原图拼帧后 1024×912 需升至 Level 4.0 */
const AVC_LEVEL_31_MAX_CODED_AREA = 921600;

export function pickAvcCodecForVap(encW: number, encH: number): string {
  if (encW * encH > AVC_LEVEL_31_MAX_CODED_AREA) {
    return "avc1.420028";
  }
  return "avc1.42001f";
}
